// Fetch wrapper for the CheckList HTTP API (PLAN.md Task 1.1).
// Sends Cognito access token (api JWT verifier uses tokenUse: "access").
// On 401: refresh once, retry once, then clear session.

import {
  encodeGuestNameHeader,
  type AdminAssistantMetrics,
  type AdminSignupStats,
  type AdminWaitlistResponse,
  type AuthConfigResponse,
  type WaitlistStatus,
  type AdminSettingsResponse,
  type AiApplyProposalRequest,
  type AiAssistantStatus,
  type AiChatSessionSummary,
  type AiChatTranscriptMode,
  type AiOutcomeEvent,
  type AiProposalOp,
  type AiUndoResult,
  type AiUndoView,
  type ListKind,
  type ResourceKind,
} from "@checklist/shared";
import {
  getAccessToken,
  isCognitoConfigured,
  refreshSession,
  signOut,
} from "./auth.js";
import { clearShareSession, getGuestIdentity, getShareSession } from "./shareSession.js";

export type { ListKind };

const BASE = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, "");

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly body: Record<string, unknown> = {}
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type JsonBody = object | unknown[] | undefined;

function resourceForPath(path: string): { kind: ResourceKind; id: string } | null {
  const match = path.match(/^\/(lists|templates|checklists)\/([^/?]+)/);
  if (!match) return null;
  const kind = match[1] === "lists" ? "list" : match[1] === "templates" ? "template" : "checklist";
  return { kind, id: decodeURIComponent(match[2]!) };
}

function shareHeaders(path: string): Record<string, string> {
  const resource = resourceForPath(path);
  if (!resource) return {};
  const token = getShareSession(resource.kind, resource.id);
  if (!token) return {};
  const guest = getGuestIdentity();
  return {
    "X-Share-Token": token,
    "X-Guest-Id": guest.guestId,
    ...(guest.displayName ? { "X-Guest-Name": encodeGuestNameHeader(guest.displayName) } : {}),
  };
}

/** Revoked, expired, or unknown share tokens. Status 403/404/410, or the share codes. */
export function isDeadShareError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return (
    err.status === 403 ||
    err.status === 404 ||
    err.status === 410 ||
    err.code === "SHARE_NOT_FOUND" ||
    err.code === "SHARE_REVOKED" ||
    err.code === "SHARE_EXPIRED"
  );
}

export function shouldClearShare(err: ApiError): boolean {
  return isDeadShareError(err);
}

async function parseBody(res: Response): Promise<Record<string, unknown>> {
  return (await res.json().catch(() => ({}))) as Record<string, unknown>;
}

async function callInner<T>(
  path: string,
  method: string,
  body?: JsonBody,
  auth = true
): Promise<T> {
  if (!BASE) throw new ApiError("VITE_API_URL is not configured", 0);

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const share = shareHeaders(path);
  const resource = resourceForPath(path);
  if (auth) {
    const token = getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    Object.assign(headers, share);
    if (!token && !Object.keys(share).length) throw new ApiError("Not signed in", 401);
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (networkErr) {
    const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
    throw new ApiError(`Network error calling ${path}: ${msg}`, 0);
  }

  if (res.status === 401 && auth && getAccessToken() && isCognitoConfigured()) {
    const ok = await refreshSession();
    if (ok) {
      const token = getAccessToken();
      if (token) {
        const retryHeaders = { ...headers, Authorization: `Bearer ${token}` };
        const retry = await fetch(`${BASE}${path}`, {
          method,
          headers: retryHeaders,
          body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (retry.status === 401) {
          signOut();
          throw new ApiError("Session expired — please sign in again", 401);
        }
        const retryData = await parseBody(retry);
        if (!retry.ok) {
          const error = new ApiError(
            (retryData.error as string) ?? `Request failed (${retry.status})`,
            retry.status,
            retryData.error as string | undefined,
            retryData
          );
          if (resource && shouldClearShare(error) && Object.keys(share).length) clearShareSession(resource.kind, resource.id);
          throw error;
        }
        return retryData as T;
      }
    }
    signOut();
    throw new ApiError("Session expired — please sign in again", 401);
  }

  const data = await parseBody(res);
  if (!res.ok) {
    const error = new ApiError(
      (data.error as string) ?? `Request failed (${res.status})`,
      res.status,
      data.error as string | undefined,
      data
    );
    if (resource && shouldClearShare(error) && Object.keys(share).length) clearShareSession(resource.kind, resource.id);
    throw error;
  }
  return data as T;
}

export async function apiGet<T>(path: string, auth = true): Promise<T> {
  return callInner<T>(path, "GET", undefined, auth);
}

export async function apiPost<T>(path: string, body?: JsonBody, auth = true): Promise<T> {
  return callInner<T>(path, "POST", body, auth);
}

export async function apiPut<T>(path: string, body?: JsonBody, auth = true): Promise<T> {
  return callInner<T>(path, "PUT", body, auth);
}

export async function apiDelete<T>(path: string, auth = true): Promise<T> {
  return callInner<T>(path, "DELETE", undefined, auth);
}

export interface ApiShareResolution { kind: ResourceKind; id: string; name: string; role: ApiChecklistRole; requireSignIn: boolean; ownerScreenName: string }
export async function resolveShareLink(token: string): Promise<ApiShareResolution> {
  // A bearer session is useful only for require-sign-in links; use it when available.
  return apiGet<ApiShareResolution>(`/share/${encodeURIComponent(token)}`, Boolean(getAccessToken()));
}

/** Role from a locally stored share token, or null. A dead token (revoked,
 *  expired, unknown) is removed; any failure is non-fatal for the caller (D3). */
export async function resolveStoredShareRole(
  kind: ResourceKind,
  id: string
): Promise<ApiChecklistRole | null> {
  const token = getShareSession(kind, id);
  if (!token) return null;
  try {
    return (await resolveShareLink(token)).role;
  } catch (err) {
    if (isDeadShareError(err)) clearShareSession(kind, id);
    return null;
  }
}

export interface ApiShareLink { token: string; role: ApiChecklistRole; requireSignIn: boolean; label?: string; createdAt: string; expiresAt?: string; revokedAt?: string; useCount: number; lastUsedAt?: string; url: string }
export async function fetchShareLinks(kind: ResourceKind, id: string): Promise<ApiShareLink[]> {
  const segment = kind === "list" ? "lists" : kind === "template" ? "templates" : "checklists";
  return (await apiGet<{ shareLinks: ApiShareLink[] }>(`/${segment}/${encodeURIComponent(id)}/share-links`)).shareLinks ?? [];
}
export async function createShareLink(kind: ResourceKind, id: string, body: { role: ApiChecklistRole; requireSignIn?: boolean; expiresInDays?: number; label?: string }): Promise<ApiShareLink> {
  const segment = kind === "list" ? "lists" : kind === "template" ? "templates" : "checklists";
  return apiPost<ApiShareLink>(`/${segment}/${encodeURIComponent(id)}/share-links`, body);
}
function resourceSegment(kind: ResourceKind): string {
  return kind === "list" ? "lists" : kind === "template" ? "templates" : "checklists";
}
function resourceBase(kind: ResourceKind, id: string): string {
  return `/${resourceSegment(kind)}/${encodeURIComponent(id)}`;
}
export async function revokeShareLink(kind: ResourceKind, id: string, token: string): Promise<void> {
  await apiDelete(`${resourceBase(kind, id)}/share-links/${encodeURIComponent(token)}`);
}
export async function revokeAllShareLinks(kind: ResourceKind, id: string): Promise<{ revoked: number }> {
  return apiDelete(`${resourceBase(kind, id)}/share-links`);
}
export async function rotateShareLink(kind: ResourceKind, id: string, token: string): Promise<ApiShareLink> {
  return apiPost(`${resourceBase(kind, id)}/share-links/${encodeURIComponent(token)}/rotate`, {});
}
export interface ApiEmailInvitation { email: string; role: ApiChecklistRole; createdAt: string; emailDelivered: boolean; url?: string; accepted: boolean }
export async function fetchInvitations(kind: ResourceKind, id: string): Promise<ApiEmailInvitation[]> {
  return (await apiGet<{ invitations: ApiEmailInvitation[] }>(`${resourceBase(kind, id)}/invitations`)).invitations ?? [];
}
export async function createInvitation(kind: ResourceKind, id: string, body: { email: string; role: ApiChecklistRole }): Promise<{ emailDelivered: boolean; url: string }> {
  return apiPost(`${resourceBase(kind, id)}/invitations`, body);
}
export async function revokeInvitation(kind: ResourceKind, id: string, email: string): Promise<void> {
  await apiDelete(`${resourceBase(kind, id)}/invitations/${encodeURIComponent(email)}`);
}
export async function fetchResourceMembers(kind: ResourceKind, id: string): Promise<ApiMember[]> {
  return (await apiGet<{ members: ApiMember[] }>(`${resourceBase(kind, id)}/members`)).members ?? [];
}
export async function removeResourceMember(kind: ResourceKind, id: string, sub: string): Promise<void> {
  await apiDelete(`${resourceBase(kind, id)}/members/${encodeURIComponent(sub)}`);
}
export interface ApiResourcePreview { kind: ResourceKind; name: string; itemCount: number; ownerScreenName: string }
export async function fetchResourcePreview(kind: ResourceKind, id: string): Promise<ApiResourcePreview> {
  return apiGet(`/resources/${kind}/${encodeURIComponent(id)}/preview`, false);
}
export async function createAccessRequest(body: { kind: ResourceKind; id: string; email: string; name?: string; message?: string }): Promise<{ status: string; emailDelivered?: boolean; duplicate?: boolean }> {
  return apiPost("/access-requests", body, false);
}
export interface ApiAccessDecision {
  resourceKind: ResourceKind;
  resourceId: string;
  resourceName: string;
  requesterEmail: string;
  requesterName?: string;
  message?: string;
  status: string;
  alreadyDecided?: boolean;
}
export async function fetchAccessDecision(token: string): Promise<ApiAccessDecision> {
  return apiGet(`/access-decisions/${encodeURIComponent(token)}`, false);
}
export async function decideAccessRequest(token: string, body: { decision: "approve" | "deny"; role?: ApiChecklistRole }): Promise<{ status: string }> {
  return apiPost(`/access-decisions/${encodeURIComponent(token)}`, body, false);
}
export interface ApiAccessRequest {
  requestId: string;
  requesterEmail: string;
  requesterName?: string;
  message?: string;
  status: "pending" | "approved" | "denied";
  decidedAt?: string;
  decidedRole?: ApiChecklistRole;
  createdAt: string;
}
export async function fetchAccessRequests(kind: ResourceKind, id: string): Promise<ApiAccessRequest[]> {
  return (
    await apiGet<{ accessRequests: ApiAccessRequest[] }>(
      `${resourceBase(kind, id)}/access-requests`
    )
  ).accessRequests ?? [];
}
export async function decideAccessRequestInPanel(kind: ResourceKind, id: string, requestId: string, body: { decision: "approve" | "deny"; role?: ApiChecklistRole }): Promise<{ status: string }> {
  return apiPost(`${resourceBase(kind, id)}/access-requests/${encodeURIComponent(requestId)}`, body);
}

/** Claim pending email grants after login (Phase 4). 404-tolerant no-op until then. */
export async function claimGrants(): Promise<void> {
  try {
    await apiPost("/grants/claim", {});
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 501)) return;
    // Other errors (401 already signed out): ignore for login UX until Phase 4.
    if (err instanceof ApiError && err.status >= 400 && err.status < 500) return;
    // Network / 5xx: swallow — grants claim is best-effort at login.
  }
}

export interface MeProfile {
  sub: string;
  groups: string[];
  screenName: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarColor?: string;
  avatarKey?: string;
  googlePicture?: string;
  createdAt: string;
  updatedAt?: string;
}

export async function fetchMe(): Promise<MeProfile> {
  return apiGet<MeProfile>("/me");
}

export interface ProfileUpdate {
  screenName: string;
  /** "" or null clears the stored value. */
  firstName?: string | null;
  lastName?: string | null;
  avatarColor?: string | null;
}

export async function updateMyProfile(update: ProfileUpdate): Promise<MeProfile> {
  return apiPut<MeProfile>("/me", update);
}

export interface AvatarUpload {
  url: string;
  fields: Record<string, string>;
}

export async function getAvatarUploadUrl(): Promise<{ upload: AvatarUpload; avatarKey: string }> {
  return apiPost<{ upload: AvatarUpload; avatarKey: string }>("/me/avatar-url", {});
}

export async function deleteAvatar(): Promise<MeProfile> {
  return apiDelete<MeProfile>("/me/avatar");
}

/**
 * POST JPEG bytes with the presigned form. Fields must precede the file;
 * S3 ignores anything after it. The browser sets the multipart boundary.
 */
export async function uploadAvatarJpeg(upload: AvatarUpload, blob: Blob): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(upload.fields)) {
    form.append(key, value);
  }
  form.append("file", blob);

  let res: Response;
  try {
    res = await fetch(upload.url, { method: "POST", body: form });
  } catch (networkErr) {
    const msg = networkErr instanceof Error ? networkErr.message : String(networkErr);
    throw new ApiError(`Network error uploading avatar: ${msg}`, 0);
  }
  if (!res.ok) {
    throw new ApiError(`Avatar upload failed (${res.status})`, res.status);
  }
}

// --- Lists (PLAN.md Task 2.1 / 2.2; D-29 kind + subgroups) ---

export interface ApiListItem {
  id: string;
  text: string;
  note?: string;
}

export interface ApiListSubgroup {
  id: string;
  name: string;
  items: ApiListItem[];
}

export interface ApiList {
  id: string;
  name: string;
  nameLower: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  items: ApiListItem[];
  subgroups: ApiListSubgroup[];
  isPublished: boolean;
  copyCount: number;
  aiGenerated?: boolean;
  sourceListId?: string;
  ownerId: string;
  ownerScreenName: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  memberCount?: number;
  activeShareCount?: number;
}

/** Collection DTO for GET /lists — cards and pickers. GET-by-id stays ApiList. */
export interface ApiListSummary {
  id: string;
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  itemCount: number;
  preview: string[];
  isPublished: boolean;
  ownerId: string;
  memberCount?: number;
}

export type ApiCommunityListDetail = ApiList & { official?: boolean };

/** Collection DTO for GET /community/lists. */
export interface ApiCommunityListSummary {
  official?: boolean;
  id: string;
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  itemCount: number;
  ownerId: string;
  ownerScreenName: string;
  copyCount: number;
  aiGenerated?: boolean;
}

export interface ListWriteBody {
  name: string;
  description?: string;
  tags?: string[];
  kind?: ListKind;
  items?: Array<{ id?: string; text: string; note?: string }>;
  subgroups?: Array<{
    id?: string;
    name: string;
    items: Array<{ id?: string; text: string; note?: string }>;
  }>;
  /** Omit to write unguarded; send the version last read to get 409 STALE_VERSION on a conflict. */
  version?: number;
}

export async function fetchLists(): Promise<ApiListSummary[]> {
  const res = await apiGet<{ lists: ApiListSummary[] }>("/lists");
  return res.lists ?? [];
}

export async function fetchList(id: string): Promise<ApiList> {
  return apiGet<ApiList>(`/lists/${encodeURIComponent(id)}`);
}

export async function createList(body: ListWriteBody): Promise<ApiList> {
  return apiPost<ApiList>("/lists", { ...body });
}

export async function updateList(id: string, body: ListWriteBody): Promise<ApiList> {
  return apiPut<ApiList>(`/lists/${encodeURIComponent(id)}`, { ...body });
}

export async function deleteList(id: string): Promise<void> {
  await apiDelete<unknown>(`/lists/${encodeURIComponent(id)}`);
}

export async function publishList(id: string): Promise<ApiList> {
  return apiPost<ApiList>(`/lists/${encodeURIComponent(id)}/publish`, {});
}

export async function unpublishList(id: string): Promise<ApiList> {
  return apiDelete<ApiList>(`/lists/${encodeURIComponent(id)}/publish`);
}

// --- Community (PLAN.md Task 5.1) ---

export interface CommunityBrowseResult {
  lists: ApiCommunityListSummary[];
  cursor?: string;
}

export async function fetchCommunityLists(opts?: {
  q?: string;
  cursor?: string;
  category?: string;
}): Promise<CommunityBrowseResult> {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.category) params.set("category", opts.category);
  if (opts?.cursor) params.set("cursor", opts.cursor);
  const qs = params.toString();
  // Public browse (D-31 / Task 4.3) — no Authorization header.
  return apiGet<CommunityBrowseResult>(`/community/lists${qs ? `?${qs}` : ""}`, false);
}

export async function fetchCommunityList(id: string): Promise<ApiCommunityListDetail> {
  // Public detail (D-31 / Task 4.3) — no Authorization header.
  return apiGet<ApiCommunityListDetail>(`/community/lists/${encodeURIComponent(id)}`, false);
}

export async function copyCommunityList(id: string): Promise<ApiList> {
  return apiPost<ApiList>(`/community/lists/${encodeURIComponent(id)}/copy`, {});
}

export async function startCommunityList(id: string, name: string): Promise<ApiChecklist> {
  const res = await apiPost<{ checklist: ApiChecklist }>(
    `/community/lists/${encodeURIComponent(id)}/start`,
    { name }
  );
  return res.checklist;
}

export interface ApiCommunityTemplateSummary {
  official?: boolean;
  id: string;
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  sectionNames: string[];
  ownerScreenName: string;
  copyCount: number;
}

export interface ApiCommunityTemplateDetail {
  official?: boolean;
  id: string;
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  ownerScreenName: string;
  copyCount: number;
  sections: Array<
    | {
        type: "list";
        listId: string;
        name: string;
        items: Array<{ id: string; text: string }>;
        subgroups: Array<{
          id: string;
          name: string;
          items: Array<{ id: string; text: string }>;
        }>;
      }
    | { type: "inline"; name: string; items: Array<{ id: string; text: string }> }
  >;
}

export interface CommunityTemplateBrowseResult {
  templates: ApiCommunityTemplateSummary[];
}

export async function fetchCommunityTemplates(opts?: {
  q?: string;
  category?: string;
}): Promise<CommunityTemplateBrowseResult> {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.category) params.set("category", opts.category);
  const qs = params.toString();
  return apiGet<CommunityTemplateBrowseResult>(
    `/community/templates${qs ? `?${qs}` : ""}`,
    false
  );
}

export async function fetchCommunityTemplate(id: string): Promise<ApiCommunityTemplateDetail> {
  return apiGet<ApiCommunityTemplateDetail>(
    `/community/templates/${encodeURIComponent(id)}`,
    false
  );
}

export async function copyCommunityTemplate(
  id: string
): Promise<{ template: ApiTemplate; warnings: string[] }> {
  return apiPost<{ template: ApiTemplate; warnings: string[] }>(
    `/community/templates/${encodeURIComponent(id)}/copy`,
    {}
  );
}

export async function startCommunityTemplate(
  id: string,
  name: string
): Promise<{ checklist: ApiChecklist; warnings: string[] }> {
  return apiPost<{ checklist: ApiChecklist; warnings: string[] }>(
    `/community/templates/${encodeURIComponent(id)}/start`,
    { name }
  );
}

// --- AI generation (PLAN.md Task 5.2 / 5.3) ---

export interface AiGenerateResult {
  list: ApiList;
  cached: boolean;
  /** Short Bedrock model id, or `"cache"` on a cache hit (feedback envelope). */
  modelId: string;
}

export async function generateAiList(body: {
  purpose: string;
  details?: string;
}): Promise<AiGenerateResult> {
  return apiPost<AiGenerateResult>("/ai/generate", { ...body });
}

/** Public cache-only AI demo (D-37 / Task 4.3). Never invokes Bedrock. */
export interface AiDemoResult {
  cached: boolean;
  name?: string;
  items?: string[];
  listId?: string;
}

export async function aiDemo(purpose: string): Promise<AiDemoResult> {
  return apiPost<AiDemoResult>("/ai/demo", { purpose }, false);
}

// --- Admin settings (PLAN.md Task 5.4) ---

export interface AdminSettings {
  bedrockModelId: string;
  aiDailyLimit: number;
  /** Chat assistant model, separate from bedrockModelId (D-A11). */
  aiChatModelId: string;
  /** Chat assistant tokens per user per day (D-A12). */
  aiChatDailyTokenLimit: number;
  /** Whether stored chat transcripts keep text or usage details only (SA15, D-R12). */
  aiChatTranscriptMode: AiChatTranscriptMode;
}

export async function fetchAdminSettings(): Promise<AdminSettings & Pick<AdminSettingsResponse, "anthropicKeyConfigured">> {
  return apiGet<AdminSettings & Pick<AdminSettingsResponse, "anthropicKeyConfigured">>("/admin/settings");
}

export async function updateAdminSettings(body: {
  bedrockModelId: string;
  aiDailyLimit: number;
  aiChatModelId: string;
  aiChatDailyTokenLimit: number;
  aiChatTranscriptMode: AiChatTranscriptMode;
}): Promise<AdminSettings> {
  return apiPut<AdminSettings>("/admin/settings", { ...body });
}

export interface AiCostUsageRow {
  date: string;
  modelId: string;
  surface: "generation" | "chat";
  requestCount: number;
  cachedListCount: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  estimatedCostUsd: number;
  cacheSavingsUsd: number;
}

export interface AdminAiCostUsageResponse {
  users: Array<{ sub: string; screenName: string; email: string; rows: AiCostUsageRow[] }>;
}

export async function fetchAdminAiCosts(days: 7 | 30 | 90): Promise<AdminAiCostUsageResponse> {
  return apiGet<AdminAiCostUsageResponse>(`/admin/ai-costs?days=${days}`);
}

export async function fetchAdminAssistantMetrics(days: 7 | 30 | 90): Promise<AdminAssistantMetrics> {
  return apiGet<AdminAssistantMetrics>(`/admin/assistant-metrics?days=${days}`);
}

export async function fetchAdminSignupStats(days: 7 | 30 | 90): Promise<AdminSignupStats> {
  return apiGet<AdminSignupStats>(`/admin/signup-stats?days=${days}`);
}

export async function fetchAuthConfig(): Promise<AuthConfigResponse> {
  return apiGet<AuthConfigResponse>("/auth/config", false);
}

export async function submitWaitlistRequest(body: {
  email: string;
  note?: string;
  website?: string;
  turnstileToken?: string;
}): Promise<{ ok: true }> {
  return apiPost<{ ok: true }>("/waitlist", body, false);
}

export interface WaitlistActionResult {
  email: string;
  status: "approved" | "denied" | "skipped";
  emailDelivered: boolean;
}

export async function fetchAdminWaitlist(status: WaitlistStatus): Promise<AdminWaitlistResponse> {
  return apiGet<AdminWaitlistResponse>(`/admin/waitlist?status=${status}`);
}

export async function approveWaitlist(emails: string[]): Promise<{ results: WaitlistActionResult[] }> {
  return apiPost<{ results: WaitlistActionResult[] }>("/admin/waitlist/approve", { emails });
}

export async function denyWaitlist(emails: string[]): Promise<{ results: WaitlistActionResult[] }> {
  return apiPost<{ results: WaitlistActionResult[] }>("/admin/waitlist/deny", { emails });
}

// --- AI chat proposals + sessions (AI_ASSISTANT_PLAN.md Task 4.2, D-A3) ---

export interface ApiProposal {
  proposalId: string;
  summary: string;
  ops: AiProposalOp[];
  createdAt: string;
  ownerSub: string;
  sessionId: string;
  appliedAt?: string;
}

export interface ApiAppliedOpResult {
  index: number;
  op: string;
  status: "applied" | "failed";
  resourceKind?: ResourceKind;
  resourceId?: string;
  error?: string;
  unmatched?: string[];
}

export interface ApiApplyProposalResult {
  proposalId: string;
  applied: number;
  results: ApiAppliedOpResult[];
}

export async function fetchProposal(id: string): Promise<ApiProposal> {
  return apiGet<ApiProposal>(`/ai/proposals/${encodeURIComponent(id)}`);
}

export async function applyProposal(
  id: string,
  body: AiApplyProposalRequest
): Promise<ApiApplyProposalResult> {
  return apiPost<ApiApplyProposalResult>(`/ai/proposals/${encodeURIComponent(id)}/apply`, body);
}

export async function discardProposal(id: string): Promise<void> {
  await apiDelete<unknown>(`/ai/proposals/${encodeURIComponent(id)}`);
}

export async function undoProposal(id: string, confirm = false): Promise<AiUndoResult> {
  return apiPost<AiUndoResult>(`/ai/proposals/${encodeURIComponent(id)}/undo`, { confirm });
}

export async function redoProposal(id: string, confirm = false): Promise<AiUndoResult> {
  return apiPost<AiUndoResult>(`/ai/proposals/${encodeURIComponent(id)}/redo`, { confirm });
}

export interface ApiChatSessionTurn {
  seq: number;
  role: "user" | "assistant";
  text: string;
  createdAt: string;
  proposalId?: string;
  toolCalls?: string[];
}

export interface ApiChatSession {
  sessionId: string;
  title: string;
  resourceKind?: ResourceKind;
  resourceId?: string;
  updatedAt: string;
  turns: ApiChatSessionTurn[];
  undo?: AiUndoView;
}

export async function fetchChatSession(id: string): Promise<ApiChatSession> {
  return apiGet<ApiChatSession>(`/ai/sessions/${encodeURIComponent(id)}`);
}

export async function listChatSessions(): Promise<AiChatSessionSummary[]> {
  const res = await apiGet<{ sessions: AiChatSessionSummary[] }>("/ai/sessions");
  return res.sessions ?? [];
}

export async function deleteChatSession(id: string): Promise<void> {
  await apiDelete<unknown>(`/ai/sessions/${encodeURIComponent(id)}`);
}

// --- AI assistant status + outcomes (AI_ASSISTANT_REMEDIATION_PLAN Task 4.1, SA1/SA18) ---

export async function fetchAiStatus(): Promise<AiAssistantStatus> {
  return apiGet<AiAssistantStatus>("/ai/status");
}

/** Best-effort (SA18): outcomes never surface an error to the caller. */
export async function postAiOutcome(event: AiOutcomeEvent): Promise<void> {
  try {
    await apiPost<unknown>("/ai/outcome", event);
  } catch (err) {
    console.error("postAiOutcome failed", err);
  }
}

// --- AI assistant knowledge base (degraded-mode search, Task 6.2, F-10, SA10) ---

export interface AiKbSearchResult {
  id: string;
  title: string;
  route?: string;
  snippet: string;
}

export interface AiKbSection {
  id: string;
  title: string;
  routes: string[];
  body: string;
}

export async function fetchAiKbSearch(q: string): Promise<AiKbSearchResult[]> {
  const res = await apiGet<{ results: AiKbSearchResult[] }>(`/ai/kb/search?q=${encodeURIComponent(q)}`);
  return res.results ?? [];
}

export async function fetchAiKbSection(id: string): Promise<AiKbSection> {
  return apiGet<AiKbSection>(`/ai/kb/sections/${encodeURIComponent(id)}`);
}

// --- Templates (PLAN.md Task 3.1 / 3.2) ---

export type ApiTemplateSection =
  | { type: "list"; listId: string; name: string }
  | { type: "inline"; name: string; items: Array<{ id: string; text: string }> };

export interface ApiTemplate {
  id: string;
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  sections: ApiTemplateSection[];
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  memberCount?: number;
  activeShareCount?: number;
}

/** Collection DTO for GET /templates — cards. GET-by-id stays ApiTemplate. */
export interface ApiTemplateSummary {
  id: string;
  name: string;
  kind: ListKind;
  sectionCount: number;
  sectionNames: string[];
  /** Ids of `type: "list"` sections, in section order (D16 / NT-014). */
  listIds: string[];
  ownerId: string;
  memberCount?: number;
}

export type TemplateWriteSection =
  | { type: "list"; listId: string }
  | { type: "inline"; name: string; items?: Array<{ id?: string; text: string }> };

export interface TemplateWriteBody {
  name: string;
  description?: string;
  tags?: string[];
  kind?: ListKind;
  sections?: TemplateWriteSection[];
  /** Omit to write unguarded; send the version last read to get 409 STALE_VERSION on a conflict. */
  version?: number;
}

export async function fetchTemplates(): Promise<ApiTemplateSummary[]> {
  const res = await apiGet<{ templates: ApiTemplateSummary[] }>("/templates");
  return res.templates ?? [];
}

export async function fetchTemplate(id: string): Promise<ApiTemplate> {
  return apiGet<ApiTemplate>(`/templates/${encodeURIComponent(id)}`);
}

export async function createTemplate(body: TemplateWriteBody): Promise<ApiTemplate> {
  return apiPost<ApiTemplate>("/templates", { ...body });
}

export async function updateTemplate(id: string, body: TemplateWriteBody): Promise<ApiTemplate> {
  return apiPut<ApiTemplate>(`/templates/${encodeURIComponent(id)}`, { ...body });
}

export async function deleteTemplate(id: string): Promise<void> {
  await apiDelete<unknown>(`/templates/${encodeURIComponent(id)}`);
}

export interface InstantiateResult {
  checklist: ApiChecklist;
  warnings: string[];
}

export async function instantiateTemplate(
  id: string,
  name: string
): Promise<InstantiateResult> {
  return apiPost<InstantiateResult>(`/templates/${encodeURIComponent(id)}/instantiate`, {
    name,
  });
}

// --- Checklists (PLAN.md Task 3.1 / 3.3) ---

export interface ApiChecklistItem {
  id: string;
  text: string;
  checked: boolean;
  checkedBy?: string;
  checkedAt?: string;
  /** Display name for anonymous guests, who have no profile to look up. */
  screenName?: string;
}

export interface ApiChecklistSubgroup {
  id: string;
  name: string;
  items: ApiChecklistItem[];
}

export interface ApiChecklistSection {
  name: string;
  items: ApiChecklistItem[];
  subgroups: ApiChecklistSubgroup[];
}

export interface ApiChecklist {
  id: string;
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  createdFromTemplateId?: string;
  sections: ApiChecklistSection[];
  memberCount: number;
  activeShareCount?: number;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  archivedAt?: string;
}

/** Collection DTO for GET /checklists — cards. GET-by-id stays ApiChecklist. */
export interface ApiChecklistSummary {
  id: string;
  name: string;
  kind: ListKind;
  checked: number;
  total: number;
  memberCount: number;
  ownerId: string;
  archivedAt?: string;
}

export interface ChecklistWriteBody {
  name?: string;
  description?: string;
  tags?: string[];
  kind?: ListKind;
  /** Omit to write unguarded; send the version last read to get 409 STALE_VERSION on a conflict. */
  version?: number;
  sections?: Array<{
    name: string;
    items: Array<{
      id?: string;
      text: string;
      checked?: boolean;
      checkedBy?: string;
      checkedAt?: string;
      screenName?: string;
    }>;
    subgroups?: Array<{
      id?: string;
      name: string;
      items: Array<{
        id?: string;
        text: string;
        checked?: boolean;
        checkedBy?: string;
        checkedAt?: string;
      }>;
    }>;
  }>;
}

export async function fetchChecklists(): Promise<ApiChecklistSummary[]> {
  const res = await apiGet<{ checklists: ApiChecklistSummary[] }>("/checklists");
  return res.checklists ?? [];
}

export async function createChecklist(input: {
  name: string;
  kind?: ListKind;
  items?: string[];
}): Promise<ApiChecklist> {
  return apiPost<ApiChecklist>("/checklists", { ...input });
}

export async function fetchChecklist(id: string): Promise<ApiChecklist> {
  return apiGet<ApiChecklist>(`/checklists/${encodeURIComponent(id)}`);
}

export async function updateChecklist(
  id: string,
  body: ChecklistWriteBody
): Promise<ApiChecklist> {
  return apiPut<ApiChecklist>(`/checklists/${encodeURIComponent(id)}`, { ...body });
}

/** Snapshot a saved List into a new, unchecked section on this Checklist. */
export async function addListSectionToChecklist(
  checklistId: string,
  listId: string,
  version: number
): Promise<ApiChecklist> {
  return apiPost<ApiChecklist>(
    `/checklists/${encodeURIComponent(checklistId)}/sections/from-list`,
    { listId, version }
  );
}

export async function deleteChecklist(id: string): Promise<void> {
  await apiDelete<unknown>(`/checklists/${encodeURIComponent(id)}`);
}

export async function setChecklistArchived(
  id: string,
  archived: boolean
): Promise<ApiChecklist> {
  return apiPost<ApiChecklist>(`/checklists/${encodeURIComponent(id)}/archive`, {
    archived,
  });
}

export async function checkChecklistItem(
  id: string,
  body: {
    sectionIdx: number;
    itemId: string;
    checked: boolean;
    subgroupId?: string | null;
  }
): Promise<ApiChecklistItem & { checklistVersion?: number }> {
  return apiPost<ApiChecklistItem & { checklistVersion?: number }>(
    `/checklists/${encodeURIComponent(id)}/check`,
    body
  );
}

// --- Sharing (PLAN.md Task 4.1 / 4.3) ---

export type ApiChecklistRole = "read" | "write";

export interface ApiMember {
  sub: string;
  role: "owner" | ApiChecklistRole;
  screenName: string;
  addedAt: string;
  firstName?: string;
  lastName?: string;
  avatarColor?: string;
  avatarKey?: string;
  googlePicture?: string;
  updatedAt?: string;
}

export interface ApiInvitePublic {
  token: string;
  checklistId: string;
  checklistName: string;
  role: ApiChecklistRole;
  expiresAt: string;
}

export interface ApiCreateInviteResult {
  token: string;
  url: string;
  role: ApiChecklistRole;
  expiresAt: string;
  maxUses: number;
}

export async function fetchChecklistMembers(checklistId: string): Promise<ApiMember[]> {
  const res = await apiGet<{ members: ApiMember[] }>(
    `/checklists/${encodeURIComponent(checklistId)}/members`
  );
  return res.members ?? [];
}

export async function removeChecklistMember(
  checklistId: string,
  memberSub: string
): Promise<void> {
  await apiDelete<unknown>(
    `/checklists/${encodeURIComponent(checklistId)}/members/${encodeURIComponent(memberSub)}`
  );
}

export async function createChecklistInvite(
  checklistId: string,
  body: { role: ApiChecklistRole; maxUses?: number }
): Promise<ApiCreateInviteResult> {
  return apiPost<ApiCreateInviteResult>(
    `/checklists/${encodeURIComponent(checklistId)}/invites`,
    body
  );
}

export async function getInvite(token: string): Promise<ApiInvitePublic> {
  return apiGet<ApiInvitePublic>(`/invites/${encodeURIComponent(token)}`, false);
}

export async function acceptInvite(
  token: string
): Promise<{ checklistId: string; role: ApiChecklistRole; alreadyMember: boolean }> {
  return apiPost(`/invites/${encodeURIComponent(token)}/accept`, {});
}

export async function createChecklistGrant(
  checklistId: string,
  body: { email: string; role: ApiChecklistRole }
): Promise<{ email: string; role: ApiChecklistRole }> {
  return apiPost(`/checklists/${encodeURIComponent(checklistId)}/grants`, body);
}
